// mdToBlocks.ts — ONE markdown-to-BlockRenderer converter for the whole app.
//
// We store some long-form content as markdown rather than as a recipe: the
// `projects.summary` column, and 12 of the 33 `skills.body` rows (the other 21
// are already JSON recipes). Both surfaces need to render it, so the converter
// lives here ONCE instead of being copy-pasted — the same anti-duplication rule
// that keeps doctrine in one place.
//
// It renders through the normal block library, so markdown content still obeys
// the three-render-path standard:
//
//   the FIRST `# ` + its paragraph  → Hero      (title + lede)
//   `#`..`####`                     → SectionHeader
//   `| a | b |` + `|---|`           → DataTable
//   `> `                            → Callout
//   `- ` / `* ` / `1. `             → a real UL (adjacent lines group into one)
//   anything else                   → P  (renders inline **bold**, `code`, links)
//
// EXTENDED 2026-08-15, because Erez looked at a project rendered from
// `projects.summary` and said it reads as text rather than as a document — and
// he was right. The row IS the source of truth for a project now (the parallel
// kb-doc/projects/* pages were deleted the same day), so the row's rendering
// has to be as good as a hand-built recipe or the decision costs him fidelity.
//
// THE FIX IS IN THE CONVERTER, NOT THE STORAGE, and that is the whole point.
// The obvious alternative — store a JSON recipe in the column, which
// `bodyToBlocks` below already supports — would give perfect fidelity and break
// the thing the column exists for: **every agent hydrates by reading
// `projects.summary` as prose.** Handing them a wall of JSON to make a web page
// prettier is trading the primary consumer for the secondary one. Markdown stays
// legible to both.
//
// Each addition is strictly additive: it only fires on syntax that renders
// BADLY today (a markdown table currently becomes a row of `| a | b |`
// paragraphs), so nothing that looks right now can start looking wrong.
//
// HISTORY (2026-07-26): the original lived in ProjectDetailPage and dumped list
// lines straight into a P, with a comment claiming "P renders `- ` lists". It
// does not — `renderRich` only handles inline bold/code/italic/links and <p>
// collapses newlines — so every bullet ran together into one wall of text on all
// 13 project pages. Emitting real UL blocks is the fix.

import type { Block } from "./BlockRenderer";

/** Convert a markdown string into BlockRenderer blocks. */
export function mdToBlocks(md: string): Block[] {
  const out: Block[] = [];
  let para: string[] = [];
  let items: string[] = [];
  let quote: string[] = [];
  let table: string[] = [];
  // A document gets ONE Hero, from its opening `# `. Later `#` headings are
  // ordinary sections — a page with three heroes is a page with none.
  let heroTaken = false;

  const flushPara = () => {
    const body = para.join("\n").trim();
    if (body) out.push({ type: "P", props: { body } });
    para = [];
  };
  const flushList = () => {
    if (items.length) out.push({ type: "UL", props: { items } });
    items = [];
  };
  const flushQuote = () => {
    const text = quote.join("\n").trim();
    if (text) out.push({ type: "Callout", props: { text } });
    quote = [];
  };
  /** `| a | b |` rows, with the `|---|` separator dropped. */
  const flushTable = () => {
    if (table.length >= 2) {
      const cells = (r: string) =>
        r.trim().replace(/^\|/, "").replace(/\|$/, "").split("|").map((c) => c.trim());
      const [head, ...body] = table;
      const rows = body.filter((r) => !/^[\s|:-]+$/.test(r)).map(cells);
      if (rows.length) {
        out.push({ type: "DataTable", props: { headers: cells(head), rows } });
        table = [];
        return;
      }
    }
    // Not a real table after all — do not silently eat the lines.
    for (const r of table) para.push(r);
    table = [];
    flushPara();
  };
  const flushAll = () => {
    flushTable();
    flushQuote();
    flushList();
    flushPara();
  };

  for (const raw of md.split("\n")) {
    // A table is a run of pipe lines; it ends at the first line that is not one.
    if (/^\s*\|.*\|\s*$/.test(raw)) {
      flushQuote();
      flushList();
      flushPara();
      table.push(raw);
      continue;
    }
    if (table.length) flushTable();

    const bq = raw.match(/^\s*>\s?(.*)$/);
    if (bq) {
      flushList();
      flushPara();
      quote.push(bq[1]);
      continue;
    }
    if (quote.length) flushQuote();

    const heading = raw.match(/^(#{1,4})\s+(.*)/);
    if (heading) {
      flushList();
      flushPara();
      const [, hashes, title] = heading;
      // The opening `# ` becomes the Hero; its lede is filled by the paragraph
      // that follows, below.
      if (hashes.length === 1 && !heroTaken && out.length === 0) {
        heroTaken = true;
        out.push({ type: "Hero", props: { title: title.trim() } });
      } else {
        out.push({ type: "SectionHeader", props: { title: title.trim() } });
      }
      continue;
    }

    // A list line: optional indent, then a bullet or an ordered marker.
    const li = raw.match(/^(\s*)(?:[-*+]|\d+\.)\s+(.*)$/);
    if (li) {
      flushPara();
      const [, indent, text] = li;
      // UL is flat, so preserve one level of nesting with a visible marker.
      const depth = Math.floor(indent.replace(/\t/g, "    ").length / 2);
      items.push(depth > 0 ? `⋯ ${text.trim()}` : text.trim());
      continue;
    }

    if (raw.trim() === "") {
      flushList();
      flushPara();
      continue;
    }

    flushList();
    para.push(raw);
  }

  flushAll();

  // The Hero's lede: the first paragraph immediately after it, promoted rather
  // than left as a stray P under the title. Only when it is genuinely the next
  // block — a Hero followed by a heading has no lede, and inventing one from
  // further down the page would put words under a title that never introduced
  // them.
  if (out.length >= 2 && out[0].type === "Hero" && out[1].type === "P") {
    const lede = (out[1].props as { body?: string }).body;
    if (lede) {
      (out[0].props as Record<string, unknown>).lede = lede;
      out.splice(1, 1);
    }
  }
  return out;
}

/**
 * Render either storage format. Long-form bodies arrive as EITHER a JSON
 * BlockRenderer recipe (`{"blocks":[…]}`) or as markdown — `skills.body` holds
 * both — so resolve to blocks without the caller having to care. Returns null
 * when there is genuinely nothing to show.
 */
export function bodyToBlocks(body: unknown): Block[] | null {
  if (body == null) return null;

  // Already-parsed recipe object.
  if (typeof body === "object") {
    const blocks = (body as { blocks?: unknown }).blocks;
    return Array.isArray(blocks) && blocks.length ? (blocks as Block[]) : null;
  }

  if (typeof body !== "string") return null;
  const text = body.trim();
  if (!text) return null;

  // A recipe stored as text.
  if (text.startsWith("{")) {
    try {
      const parsed = JSON.parse(text) as { blocks?: unknown };
      if (Array.isArray(parsed.blocks) && parsed.blocks.length) {
        return parsed.blocks as Block[];
      }
    } catch {
      // Not valid JSON after all — fall through and treat it as markdown.
    }
  }

  const blocks = mdToBlocks(text);
  return blocks.length ? blocks : null;
}
